import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { DragHandleVerticalOutline } from "../icons/icons";
import { SlotsProvider } from "../providers/SlotsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { mergeSx } from "../system";
import { Box } from "./Box";
export const resizableDrawerClasses = {
    root: "MinervaResizableDrawer-root",
    "drag-container": "MinervaResizableDrawer-dragContainer",
    "drag-indicator": "MinervaResizableDrawer-dragIndicator",
    "drag-ghost": "MinervaResizableDrawer-dragGhost",
    "drag-backdrop": "MinervaResizableDrawer-dragBackdrop",
    "horizontal-direction": "MinervaResizableDrawer-horizontalDirection",
    "vertical-direction": "MinervaResizableDrawer-verticalDirection",
    left: "MinervaResizableDrawer-left",
    right: "MinervaResizableDrawer-right",
    up: "MinervaResizableDrawer-up",
    down: "MinervaResizableDrawer-down",
};
export const resizableDrawerSlots = {
    dragIndicator: DragHandleVerticalOutline,
};
/**
 * ## Definition
 * A resizable drawer is a specialty drawer component that allows the user to resize the drawer and its content by dragging a handle.
 *
 * ## Behavior
 * - Depending on the `resizeDirection`, on initial open the `height` or `width` of the drawer will initially be set to the `size`.
 * - When the drawer is hovered the drag handle will appear.
 * - The drag handle will appear on the same side as the `resizeDirection`.
 * - On any screen size small than the 'md' breakpoint the drag indicator will alway be visible.
 *
 * ## Structure
 * - Based off of a `Box` component.
 * - Drag behavior is handled by the `react-draggable` library.
 *
 * ## Technical Limitations
 * - If the drawer strategy is relative (default), it is up to the implementation to handle the proper placement of the drawer according to the `resizeDirection`.
 * - The resizable drawer does not support resizing in multiple directions. Only one direction can be set at a time.
 * - Because resizing updates a throttled state, the drawer and the cursor may not be in sync if the mouse moved rapidly.
 */
export const ResizableDrawer = (props) => {
    const { resizeDirection = "right", size: _size = 80, duration = 150, strategy = "relative", disableResize = false, open: _open = false, onOpenChange, onResizeEnd, slots, slotProps, className, children, sx, ...rest } = useDefaultProps({ props, component: "ResizableDrawer" });
    const [open, setOpen] = useState(_open);
    const [dragging, setDragging] = useState(false);
    const [size, setSize] = useState(_size);
    useEffect(() => {
        setOpen(_open);
    }, [_open]);
    useEffect(() => {
        setSize(_size);
    }, [_size]);
    return (_jsx(SlotsProvider, { baseSlots: resizableDrawerSlots, slots: slots, children: ({ dragIndicator: DragIndicator }) => (_jsxs(Box, { className: clsx([
                resizableDrawerClasses.root,
                dragging && open && resizableDrawerClasses["drag-backdrop"],
                (resizeDirection === "right" || resizeDirection === "left") &&
                    resizableDrawerClasses["horizontal-direction"],
                (resizeDirection === "down" || resizeDirection === "up") &&
                    resizableDrawerClasses["vertical-direction"],
                className,
            ]), sx: mergeSx({
                position: strategy,
                maxWidth: "100%",
                maxHeight: "100%",
                zIndex: (theme) => theme.zIndex.drawer,
                [`&.${resizableDrawerClasses["drag-backdrop"]}`]: {
                    "&::before": {
                        content: "''",
                        position: "fixed",
                        bottom: 0,
                        right: 0,
                        top: 0,
                        left: 0,
                    },
                },
                [`&.${resizableDrawerClasses["horizontal-direction"]}`]: {
                    width: open ? `${size}px` : 0,
                    height: "100%",
                    transition: `width ${duration}ms`,
                },
                [`&.${resizableDrawerClasses["vertical-direction"]}`]: {
                    height: open ? `${size}px` : 0,
                    width: "100%",
                    transition: `height ${duration}ms`,
                },
                breakpoints: {
                    md: {
                        [`& .${resizableDrawerClasses["drag-indicator"]}`]: {
                            opacity: 0,
                            transition: "opacity 0.3s",
                        },
                        "&:hover": {
                            [`& .${resizableDrawerClasses["drag-indicator"]}`]: {
                                opacity: 1,
                            },
                        },
                        "@media (hover: none)": {
                            [`& .${resizableDrawerClasses["drag-indicator"]}`]: {
                                opacity: 1,
                            },
                        },
                    },
                },
            }, sx), ...rest, children: [children, _jsx(Draggable, { axis: resizeDirection === "left" || resizeDirection === "right" ? "x" : "y", handle: `.${resizableDrawerClasses["drag-indicator"]}`, onStart: () => setDragging(true), position: { x: 0, y: 0 }, onStop: (_, data) => {
                        setDragging(false);
                        const newSize = resizeDirection === "right"
                            ? size + data.x
                            : resizeDirection === "left"
                                ? size - data.x
                                : resizeDirection === "down"
                                    ? size + data.y
                                    : resizeDirection === "up"
                                        ? size - data.y
                                        : 0;
                        setSize(newSize > 0 ? newSize : 0);
                        onResizeEnd?.(newSize > 0 ? newSize : 0);
                    }, children: _jsxs(Box, { className: resizableDrawerClasses["drag-container"], sx: {
                            display: disableResize || !open ? "none" : "block",
                            position: "absolute",
                            top: resizeDirection === "up" ||
                                resizeDirection === "left" ||
                                resizeDirection === "right"
                                ? 0
                                : "auto",
                            right: resizeDirection === "right" ||
                                resizeDirection === "up" ||
                                resizeDirection === "down"
                                ? 0
                                : "auto",
                            bottom: resizeDirection === "down" ||
                                resizeDirection === "left" ||
                                resizeDirection === "right"
                                ? 0
                                : "auto",
                            left: resizeDirection === "left" ||
                                resizeDirection === "up" ||
                                resizeDirection === "down"
                                ? 0
                                : "auto",
                        }, children: [_jsx(Box, { className: clsx([
                                    resizableDrawerClasses["drag-ghost"],
                                    resizeDirection === "left" && resizableDrawerClasses.left,
                                    resizeDirection === "right" && resizableDrawerClasses.right,
                                    resizeDirection === "up" && resizableDrawerClasses.up,
                                    resizeDirection === "down" && resizableDrawerClasses.down,
                                ]), sx: {
                                    opacity: dragging && open ? 0.5 : 0,
                                    transition: "opacity 0.3s",
                                    width: "100%",
                                    height: "100%",
                                    borderRight: resizeDirection === "right" ? "1px dashed" : "none",
                                    borderLeft: resizeDirection === "left" ? "1px dashed" : "none",
                                    borderTop: resizeDirection === "up" ? "1px dashed" : "none",
                                    borderBottom: resizeDirection === "down" ? "1px dashed" : "none",
                                } }), _jsx(DragIndicator, { ...slotProps?.dragIndicator, className: clsx([
                                    resizableDrawerClasses["drag-indicator"],
                                    resizeDirection === "left" && resizableDrawerClasses.left,
                                    resizeDirection === "right" && resizableDrawerClasses.right,
                                    resizeDirection === "up" && resizableDrawerClasses.up,
                                    resizeDirection === "down" && resizableDrawerClasses.down,
                                    slotProps?.dragIndicator?.className,
                                ]), sx: mergeSx({
                                    position: "absolute",
                                    opacity: 1,
                                    paddingX: 0,
                                    paddingY: 2,
                                    cursor: "grab",
                                    background: (theme) => theme.palette.disabled.onMain,
                                    color: (theme) => theme.palette.disabled.main,
                                    borderRadius: (theme) => theme.radius.full,
                                    "&:active": {
                                        cursor: "grabbing",
                                    },
                                    [`&.${resizableDrawerClasses.right}`]: {
                                        top: "50%",
                                        right: 0,
                                        transform: "translate(50%, -50%)",
                                    },
                                    [`&.${resizableDrawerClasses.left}`]: {
                                        top: "50%",
                                        left: 0,
                                        transform: "translate(-50%, -50%)",
                                    },
                                    [`&.${resizableDrawerClasses.up}`]: {
                                        top: 0,
                                        left: "50%",
                                        transform: "translate(-50%, -50%) rotate(90deg)",
                                    },
                                    [`&.${resizableDrawerClasses.down}`]: {
                                        bottom: 0,
                                        left: "50%",
                                        transform: "translate(-50%, 50%) rotate(90deg)",
                                    },
                                }, slotProps?.dragIndicator?.sx) })] }) })] })) }));
};
//# sourceMappingURL=ResizableDrawer.js.map